import axios, { AxiosResponse, AxiosError } from 'axios'
import toast from 'react-hot-toast'

// Normalizar baseURL para que termine con '/'
const API_BASE_URL_RAW = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api/v1'
const API_BASE_URL = API_BASE_URL_RAW.endsWith('/') ? API_BASE_URL_RAW : `${API_BASE_URL_RAW}/`

export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  timestamp: string
  path: string
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor para manejo de errores y refresh token
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<any>>) => {
    return response
  },
  async (error: AxiosError<ApiResponse<any>>) => {
    const originalRequest = error.config as any
    
    // Log para debugging
    if (error.response) {
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    } else if (error.request) {
      console.error('API Request Error:', {
        url: error.config?.url,
        method: error.config?.method,
        request: error.request
      });
    } else {
      console.error('API Error:', error.message);
    }

    // Si es un error 401 y no es el endpoint de login
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refreshToken')
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}auth/refresh`, {
            refreshToken
          })

          const { accessToken, refreshToken: newRefreshToken } = response.data.data

          localStorage.setItem('accessToken', accessToken)
          localStorage.setItem('refreshToken', newRefreshToken)

          // Reintentar request original
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return api(originalRequest)
        } catch (refreshError) {
          // Refresh token inválido, limpiar storage y redirigir al login
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      } else {
        // No hay refresh token, redirigir al login
        localStorage.removeItem('accessToken')
        window.location.href = '/login'
      }
    }

    // Mostrar errores con toast (excepto 401 para evitar spam)
    if (error.response?.status !== 401) {
      const data: any = (error.response as any)?.data
      const message = data?.message || 'Error en la solicitud'
      toast.error(message)
    }

    return Promise.reject(error)
  }
)

export default api
